import React, { useState } from 'react';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import TimeRangeSelector from './TimeRangeSelector';
import ComplianceScoreDonutChart from './Pie/ComplianceScoreDonutChart';
import { useGetComplianceDataHandler } from '../dataHandlers/getComplianceDataHandler';
import DocumentStatusDonutChart from './Pie/DocumentStatusDonutChart';
import { formatDateToDashboardFormat } from '../../../utils/formatDate';

const ComplianceSection = ({canReadSalesInvoices, canReadPurchaseInvoices}) => {
  const [timeRange, setTimeRange] = useState('week');

  const getDateRange = (range) => {
    const now = new Date();
    const endDate = now.toISOString().split('T')[0];

    let startDateObj = new Date(now);

    switch (range) {
      case 'week':
        startDateObj.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDateObj.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDateObj.setMonth(now.getMonth() - 3);
        break;
      default:
        startDateObj.setDate(now.getDate() - 7);
    }

    const startDate = startDateObj.toISOString().split('T')[0];

    return { startDate, endDate };
  };

  const { startDate, endDate } = getDateRange(timeRange);
  const { complianceData, isLoading } = useGetComplianceDataHandler({ startDate, endDate });

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  if (!complianceData && !isLoading) {
    return (
      <Paper elevation={3} sx={{ p: 3, bgcolor: '#fff4f4' }}>
        <Typography color="error" variant="h6">
          {'No compliance data available'}
        </Typography>
      </Paper>
    );
  }

  // Map API response to component format for sales and purchases
  const salesData = {
    total: complianceData?.sales?.total || 0,
    approved: complianceData?.sales?.approved || 0,
    rejected: complianceData?.sales?.rejected || 0,
    pending: complianceData?.sales?.pending || 0,
    validationFailed: complianceData?.sales?.validation_failed || 0,
  };

  const purchasesData = {
    total: complianceData?.purchases?.total || 0,
    approved: complianceData?.purchases?.approved || 0,
    rejected: complianceData?.purchases?.rejected || 0,
    pending: complianceData?.purchases?.pending || 0,
    validationFailed: complianceData?.purchases?.validation_failed || 0,
  };

  // Combine sales and purchases for overall metrics
  const combinedData = {
    total: salesData.total + purchasesData.total,
    approved: salesData.approved + purchasesData.approved,
    rejected: salesData.rejected + purchasesData.rejected,
    pending: salesData.pending + purchasesData.pending,
    validationFailed: salesData.validationFailed + purchasesData.validationFailed,
  };

  // For TopRejectionReasonsTable - combine sales + purchases rejection data
  // Placeholder until API contract is finalised
  const rejectionReasons = Array.isArray(complianceData?.rejectionReasons) ? complianceData.rejectionReasons : [];

  const formatRangeTooltip = (range) => {
    const { startDate: rangeStart, endDate: rangeEnd } = getDateRange(range);
    return `${rangeStart} ${'to'} ${rangeEnd}`;
  };

  const timeRanges = [
    {
      label: 'Last Week',
      value: 'week',
      tooltip: formatRangeTooltip('week'),
    },
    {
      label: 'Last Month',
      value: 'month',
      tooltip: formatRangeTooltip('month'),
    },
    {
      label: 'Last 3 Months',
      value: 'quarter',
      tooltip: formatRangeTooltip('quarter'),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {'Compliance Overview'}
        </Typography>

        <TimeRangeSelector
          timeRange={timeRange}
          handleTimeRangeChange={handleTimeRangeChange}
          loading={isLoading}
          timeRanges={timeRanges}
        />
      </Stack>
      <Typography variant="body1" sx={{ mb: 1, textAlign: 'right' }}>
        {'Period:'}{' '}
        <Box component="span" fontWeight="bold" style={{ direction: 'ltr', unicodeBidi: 'bidi-override' }}>
          {formatDateToDashboardFormat(startDate)} {'to'} {formatDateToDashboardFormat(endDate)}
        </Box>
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <DocumentStatusDonutChart
            key={`sales-${timeRange}`}
            data={salesData}
            loading={isLoading}
            title={'Sales Document Status'}
            accessDenied={!canReadSalesInvoices}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <DocumentStatusDonutChart
            key={`purchases-${timeRange}`}
            data={purchasesData}
            loading={isLoading}
            title={'Purchases Document Status'}
            accessDenied={!canReadPurchaseInvoices}  //canReadPurchaseInvoice
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <ComplianceScoreDonutChart key={`compliance-${timeRange}`} data={combinedData} loading={isLoading} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComplianceSection;
